"use client";
import type React from "react";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ImageIcon, Upload, X } from "lucide-react";
import { cn } from "@/lib/utils";

interface ImageUploadProps {
  initialImage?: string;
  onImageChange: (file: File | null) => void;
  onImageRemove?: () => void;
  disabled?: boolean;
}

export const ImageUpload = ({
  initialImage,
  onImageChange,
  onImageRemove,
  disabled = false,
}: ImageUploadProps) => {
  const [preview, setPreview] = useState<string | null>(initialImage || null);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    setPreview(initialImage || null);
  }, [initialImage]);

  const processFile = (file: File | null) => {
    if (disabled) return;
    if (file) {
      const allowedFormats = ["image/jpeg", "image/jpg", "image/png"];
      if (!allowedFormats.includes(file.type)) {
        setError("Formato inválido. Apenas JPG, JPEG e PNG são permitidos.");
        return;
      }
      const maxSize = 25 * 1024 * 1024; // 25MB
      if (file.size > maxSize) {
        setError("Tamanho inválido. A imagem deve ser até 25MB.");
        return;
      }
      setError(null);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
        onImageChange(file);
      };
      reader.readAsDataURL(file);
    } else {
      setPreview(null);
      onImageChange(null);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    processFile(file);
  };

  const handleRemoveImage = () => {
    if (disabled) return;
    setPreview(null);
    onImageChange(null);
    if (onImageRemove) onImageRemove();
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    if (disabled) return;
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    if (disabled) return;
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    if (disabled) return;
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0] || null;
    processFile(file);
  };

  return (
    <div className={cn("space-y-4", disabled && "opacity-60")}>
      {!preview ? (
        <div
          className={cn(
            "flex flex-col items-center justify-center w-full h-48 border-2 border-dashed rounded-lg transition-colors",
            isDragging
              ? "border-primary bg-primary/5"
              : "border-muted-foreground/25 hover:border-primary/50 bg-muted/50",
            disabled ? "cursor-not-allowed" : "cursor-pointer"
          )}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() =>
            !disabled && document.getElementById("image-upload-input")?.click()
          }
        >
          <ImageIcon className="w-12 h-12 mb-2 text-muted-foreground" />
          <p className="text-sm text-muted-foreground mb-2">
            Arraste ou clique para selecionar
          </p>
          <p className="text-xs text-muted-foreground">JPG, PNG (máx. 25MB)</p>
          <input
            id="image-upload-input"
            type="file"
            onChange={handleFileChange}
            accept="image/jpeg, image/jpg, image/png"
            className="hidden"
            disabled={disabled}
          />
        </div>
      ) : (
        <div className="relative w-full max-w-md mx-auto">
          <div className="relative rounded-lg overflow-hidden border border-muted shadow-sm">
            <img
              src={preview || "/placeholder.svg"}
              alt="Preview"
              className="w-full h-auto object-contain max-h-64"
            />
            {!disabled && (
              <div className="absolute top-2 right-2 flex gap-2">
                <Button
                  type="button"
                  variant="destructive"
                  size="icon"
                  onClick={handleRemoveImage}
                  className="h-8 w-8 rounded-full shadow-md"
                >
                  <X className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="icon"
                  onClick={() =>
                    document
                      .getElementById("image-upload-input-replace")
                      ?.click()
                  }
                  className="h-8 w-8 rounded-full shadow-md"
                >
                  <Upload className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
          <input
            id="image-upload-input-replace"
            type="file"
            onChange={handleFileChange}
            accept="image/jpeg, image/jpg, image/png"
            className="hidden"
            disabled={disabled}
          />
        </div>
      )}
      {error && (
        <div className="bg-destructive/10 text-destructive text-sm p-2 rounded-md">
          {error}
        </div>
      )}
    </div>
  );
};
